import { WebshopProductDetail } from '@cldsystems/ws-api-client';
import { useMemo } from 'react';
import { FormattedVariant } from '@/app/[locale]/product/[metaUrl]/get-variants';
import { ProductDetailContent } from '@/configs/product-general-info/product-general-info.config';
import { useAppContext } from '@/providers/app-provider/app-provider';

type ProductDetailPageContext = {
  product: WebshopProductDetail;
  variants: FormattedVariant[];
  productDetailContent: ProductDetailContent;
  /**
   * Every locale's product id for this SKU — reviews are keyed to one of these
   * per language and have to be read across all of them. Absent in the studio
   * preview, which does not query reviews at all.
   */
  reviewProductIds?: string[];
};

const useProductDetailPageContext = () => {
  const context = useAppContext<ProductDetailPageContext>();

  if (!context.product) throw new Error('Could not find product in the <Page /> context');
  if (!context.variants) throw new Error('Could not find variants in the <Page /> context');
  if (context.productDetailContent === undefined)
    throw new Error('Could not find generalInfos in the <Page /> context');

  return context;
};

/** Falls back to the current locale's product when the page supplied no sibling ids. */
export const useReviewProductIds = (): string[] => {
  const { product, reviewProductIds } = useProductDetailPageContext();

  // Kept referentially stable — these ids drive query keys and `useQueries` lists.
  return useMemo(
    () => (reviewProductIds?.length ? reviewProductIds : [product.id]),
    [reviewProductIds, product.id],
  );
};

export default useProductDetailPageContext;
